import { ApiError, apiFetch, authHeader, getApiBaseUrl } from "./config.js";
/**
 * Uploads a document for the ingestion pipeline to process. Not routed
 * through `apiFetch` - that helper always JSON-encodes its body, and a file
 * upload needs a multipart form body instead - but it throws the same
 * `ApiError` on a non-2xx response, so callers handle both the same way.
 * Returns immediately (the real endpoint responds 202) with the job's
 * initial state; poll `getIngestionJob(id)` for progress.
 */
export async function uploadIngestionDocument(file, metadata = {}, signal) {
    return postUpload("/ingestion/documents", file, metadata, signal);
}
/**
 * Uploads a .zip of PDFs. The server finds every PDF inside (any folder depth), makes one job for each and reads
 * them one by one in the archive's folder-and-number order; the owner, type, state and notes given apply to every
 * PDF. Returns at once with the jobs (poll each with `getIngestionJob`) and the files that were skipped, with the reason.
 */
export async function uploadIngestionArchive(file, metadata = {}, signal) {
    return postUpload("/ingestion/archives", file, metadata, signal);
}
async function postUpload(path, file, metadata, signal) {
    const form = new FormData();
    form.append("file", file);
    if (metadata.source_label)
        form.append("source_label", metadata.source_label);
    if (metadata.case_reference)
        form.append("case_reference", metadata.case_reference);
    if (metadata.notes)
        form.append("notes", metadata.notes);
    if (metadata.owner)
        form.append("owner", metadata.owner);
    if (metadata.doc_type)
        form.append("doc_type", metadata.doc_type);
    if (metadata.state)
        form.append("state", metadata.state);
    if (metadata.party_name)
        form.append("party_name", metadata.party_name);
    if (metadata.skip_processed)
        form.append("skip_processed", "true");
    const url = `${getApiBaseUrl()}${path}`;
    const response = await fetch(url, { method: "POST", body: form, signal, headers: authHeader() });
    const text = await response.text();
    // A proxy in front of the API answers an oversized or timed-out upload with an HTML page, not JSON.
    let parsed;
    try {
        parsed = text ? JSON.parse(text) : undefined;
    }
    catch {
        parsed = undefined;
    }
    if (!response.ok) {
        const detail = parsed?.detail;
        const reason = typeof detail === "string"
            ? detail
            : response.status === 413
                ? "the file is larger than the server accepts"
                : response.status === 502 || response.status === 504
                    ? "the server did not answer in time"
                    : `status ${response.status}`;
        throw new ApiError(`Upload failed: ${reason}`, response.status, url, parsed);
    }
    if (parsed === undefined) {
        throw new ApiError("Upload failed: the server sent an unreadable reply", response.status, url, undefined);
    }
    return parsed;
}
/** Every document uploaded so far, newest first. Wrap in `useQuery` with queryKey `["ingestion", "documents"]`. */
export async function listIngestionJobs(signal, archived = false) {
    const url = `${getApiBaseUrl()}/ingestion/documents${archived ? "?archived=true" : ""}`;
    const response = await fetch(url, { signal, headers: authHeader() });
    const parsed = await response.json();
    if (!response.ok) {
        throw new ApiError(`Request to ${url} failed with status ${response.status}`, response.status, url, parsed);
    }
    return parsed;
}
/**
 * One upload's current state. Poll this (e.g. a `useQuery` with
 * `refetchInterval` that stops once `stage` reaches a terminal value) to
 * show live progress instead of a client-side simulation.
 */
export async function getIngestionJob(jobId, signal) {
    const url = `${getApiBaseUrl()}/ingestion/documents/${jobId}`;
    const response = await fetch(url, { signal, headers: authHeader() });
    const parsed = await response.json();
    if (!response.ok) {
        throw new ApiError(`Request to ${url} failed with status ${response.status}`, response.status, url, parsed);
    }
    return parsed;
}
/** The "glass box" trace for one job. Admin-only. */
/** Stop a running job cooperatively (pipeline exits between stages). */
export async function cancelIngestionJob(jobId, signal) {
    return apiFetch(`/ingestion/documents/${jobId}/cancel`, { method: "POST", signal });
}
/** Remove a finished or cancelled job from the list. */
export async function deleteIngestionJob(jobId, signal) {
    const url = `${getApiBaseUrl()}/ingestion/documents/${jobId}`;
    const response = await fetch(url, { method: "DELETE", signal, headers: authHeader() });
    if (!response.ok) {
        const text = await response.text();
        const parsed = text ? JSON.parse(text) : undefined;
        throw new ApiError(`Request to ${url} failed with status ${response.status}`, response.status, url, parsed);
    }
}
export async function getIngestionTrace(jobId, signal) {
    const url = `${getApiBaseUrl()}/ingestion/documents/${jobId}/trace`;
    const response = await fetch(url, { signal, headers: authHeader() });
    const parsed = await response.json();
    if (!response.ok) {
        throw new ApiError(`Request to ${url} failed with status ${response.status}`, response.status, url, parsed);
    }
    return parsed;
}
export async function getFindingSourceDocument(findingId, signal) {
    const url = `${getApiBaseUrl()}/ingestion/findings/${findingId}/document`;
    const response = await fetch(url, { signal, headers: authHeader() });
    if (response.status === 404)
        return null;
    if (!response.ok) {
        const text = await response.text();
        const parsed = text ? JSON.parse(text) : undefined;
        throw new ApiError(`Request to ${url} failed with status ${response.status}`, response.status, url, parsed);
    }
    const blob = await response.blob();
    const pageStart = response.headers.get("X-Source-Page-Start");
    const pageEnd = response.headers.get("X-Source-Page-End");
    return {
        blob,
        contentType: response.headers.get("Content-Type") ?? "application/octet-stream",
        pageStart: pageStart ? Number(pageStart) : null,
        pageEnd: pageEnd ? Number(pageEnd) : null,
        paragraphRef: response.headers.get("X-Source-Paragraph-Ref"),
    };
}
/**
 * Shared by the finding and document-section variants: GETs a `.../-url`
 * endpoint, maps a 404 to `null` ("this system has no stored document"),
 * and makes the returned URL absolute.
 */
export async function fetchSourceDocumentLink(path, signal) {
    const url = `${getApiBaseUrl()}${path}`;
    const response = await fetch(url, { signal, headers: authHeader() });
    if (response.status === 404)
        return null;
    const text = await response.text();
    const parsed = text ? JSON.parse(text) : undefined;
    if (!response.ok) {
        throw new ApiError(`Request to ${url} failed with status ${response.status}`, response.status, url, parsed);
    }
    const wire = parsed;
    return {
        url: `${getApiBaseUrl()}${wire.url}`,
        expiresAt: wire.expires_at,
        contentType: wire.content_type,
        fileName: wire.file_name,
        sizeBytes: wire.size_bytes,
        pageStart: wire.page_start,
        pageEnd: wire.page_end,
        paragraphRef: wire.paragraph_ref,
    };
}
/**
 * The fast path for opening a Finding's source document: a signed,
 * Range-capable URL (see `SourceDocumentLink`) or `null` when there is no
 * stored document. Prefer this over `getFindingSourceDocument`, which
 * downloads the whole file as a Blob.
 */
export function getFindingSourceDocumentLink(findingId, signal) {
    return fetchSourceDocumentLink(`/ingestion/findings/${findingId}/document-url`, signal);
}
/**
 * The same fast path as `getFindingSourceDocumentLink`, for the real
 * company-document PDF an AgreementClause was extracted from.
 */
export function getAgreementClauseSourceDocumentLink(clauseId, signal) {
    return fetchSourceDocumentLink(`/agreement-clauses/${clauseId}/document-url`, signal);
}
async function sendJson(path, method, signal) {
    const url = `${getApiBaseUrl()}${path}`;
    const response = await fetch(url, { method, signal, headers: authHeader() });
    const text = await response.text();
    const parsed = text ? JSON.parse(text) : undefined;
    if (!response.ok)
        throw new ApiError(`Request to ${url} failed with status ${response.status}`, response.status, url, parsed);
    return parsed;
}
/** What the file produced (what Delete removes and Archive hides), for the confirm popup. */
export function getIngestionJobImpact(jobId, signal) {
    return sendJson(`/ingestion/documents/${jobId}/impact`, "GET", signal);
}
/** Hide a finished file and everything read from it, everywhere. Reversible. */
export function archiveIngestionJob(jobId, signal) {
    return sendJson(`/ingestion/documents/${jobId}/archive`, "POST", signal);
}
/** Bring an archived file and its data back. */
export function unarchiveIngestionJob(jobId, signal) {
    return sendJson(`/ingestion/documents/${jobId}/unarchive`, "POST", signal);
}
async function postJson(path, body, signal) {
    const url = `${getApiBaseUrl()}${path}`;
    const response = await fetch(url, { method: "POST", signal, headers: { ...authHeader(), "Content-Type": "application/json" }, body: JSON.stringify(body) });
    const text = await response.text();
    const parsed = text ? JSON.parse(text) : undefined;
    if (!response.ok)
        throw new ApiError(`Request to ${url} failed with status ${response.status}`, response.status, url, parsed);
    return parsed;
}
/** What several files produced, summed, for the batch confirm popup. */
export function getIngestionJobsBatchImpact(ids, signal) {
    return postJson("/ingestion/documents/batch-impact", { ids }, signal);
}
/** Read finished files again from the stored copy (no new upload); one that cannot be read again does not stop the rest. */
export function reprocessIngestionJobs(ids, scope = "full", signal) {
    return postJson("/ingestion/documents/reprocess", { ids, scope }, signal);
}
/** Stop several running files; one that has already finished does not stop the rest. */
export function cancelIngestionJobs(ids, signal) {
    return postJson("/ingestion/documents/cancel", { ids }, signal);
}
/** Delete, archive or unarchive several files; one that cannot be changed does not stop the rest. */
export function batchIngestionJobs(action, ids, signal) {
    return postJson("/ingestion/documents/batch", { action, ids }, signal);
}
